import { type InputHTMLAttributes, type JSX, useId } from 'react';

import { cn } from '../lib/cn';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  name: string;
  /** Guidance shown while the field is valid. */
  hint?: string;
  error?: string;
}

/**
 * A labelled input that reports its own errors.
 *
 * The label is wired to the input rather than placed beside it, and the error
 * carries `role="alert"` so it is spoken as well as shown. An error announced
 * only by turning a border red is one that a colour blind user — or anyone
 * reading a washed out screen in a kitchen — never receives at all.
 */
export function Field({
  label,
  name,
  hint,
  error,
  className,
  required,
  ...props
}: FieldProps): JSX.Element {
  // Generated rather than derived from the name, so the same field can appear
  // twice on a page without two elements claiming the same id.
  const inputId = useId();
  const messageId = `${inputId}-message`;

  // One message at a time: two competing explanations help nobody.
  const message = error ?? hint;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-slate-900">
        {label}
        {required === true ? (
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        ) : null}
      </label>

      <input
        id={inputId}
        name={name}
        required={required}
        aria-invalid={hasError}
        aria-describedby={message === undefined ? undefined : messageId}
        className={cn(
          'h-11 rounded-md border px-3 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          hasError
            ? 'border-red-500 focus-visible:ring-red-500'
            : 'border-slate-300 focus-visible:ring-slate-900',
          className,
        )}
        {...props}
      />

      {message === undefined ? null : (
        <p
          id={messageId}
          role={hasError ? 'alert' : undefined}
          className={cn('text-xs', hasError ? 'text-red-600' : 'text-slate-500')}
        >
          {message}
        </p>
      )}
    </div>
  );
}
